import {
  FavoriteFacilityEntryFragment,
  FavoriteLocationEntryFragment,
  FavoriteOrganizationEntryFragment,
  FavoritesQuery,
  FavoriteThemeEntryFragment,
  SearchOrganizationEntryFragment,
} from '@wwtr/generated';
import NextLink from 'next/link';
import React, { PropsWithChildren } from 'react';
import { Heading, Link } from '@wwtr/components/ui';

export type FavoritesListingContainerProps = PropsWithChildren<{
  data: FavoritesQuery;
  isLoading: boolean;
}>;

export type LocationWithOrganizationEntry = FavoriteLocationEntryFragment & {
  organization?: FavoriteOrganizationEntryFragment;
  facility?: FavoriteFacilityEntryFragment;
};

const combineLocationsWithOrganizations = (
  locations: FavoriteLocationEntryFragment[],
  organizations: FavoriteOrganizationEntryFragment[]
): LocationWithOrganizationEntry[] =>
  locations?.map(location => ({
    ...location,
    organization: organizations?.find(organization =>
      organization.locations.find(orgLocation => orgLocation.id === location.id)
    ),
  }));

const combineLocationsWithFacilities = (
  locations: FavoriteLocationEntryFragment[],
  facilities: FavoriteFacilityEntryFragment[]
): LocationWithOrganizationEntry[] =>
  locations?.map(location => ({
    ...location,
    facility: facilities?.find(facility =>
      (facility?.organization as SearchOrganizationEntryFragment[])?.find(
        organization =>
          organization?.locations.find(
            orgLocation => orgLocation.id === location.id
          )
      )
    ),
  }));

export const FavoritesListingContainer = ({
  data,
  isLoading = true,
}: FavoritesListingContainerProps) => {
  const favorites = {
    themes: data?.themes as FavoriteThemeEntryFragment[],
    organizations: data?.organizations as FavoriteOrganizationEntryFragment[],
    facilities: data?.facilities as FavoriteFacilityEntryFragment[],
    organizationLocations: combineLocationsWithOrganizations(
      data?.organizationLocations as FavoriteLocationEntryFragment[],
      data?.organizationLocationsEntries as FavoriteOrganizationEntryFragment[]
    ),
    facilityLocations: combineLocationsWithFacilities(
      data?.facilityLocations as FavoriteLocationEntryFragment[],
      data?.facilityLocationsEntries as FavoriteFacilityEntryFragment[]
    ),
  };

  if (isLoading) {
    return null;
  }

  return (
    <>
      {favorites?.organizations?.length > 0 && (
        <div className="mb-8">
          <Heading level={3} className="mb-4">
            Organisaties
          </Heading>
          <ul>
            {favorites?.organizations.map((organization, index) => (
              <li key={`fav_organization_${index}`}>
                <NextLink href={organization?.uri ?? ''} passHref>
                  <Link color="powerblue" customUnderline>
                    {organization?.title}
                  </Link>
                </NextLink>
              </li>
            ))}
          </ul>
        </div>
      )}

      {favorites?.organizationLocations?.length > 0 && (
        <div className="mb-8">
          <Heading level={3} className="mb-4">
            Organisaties (locaties)
          </Heading>
          <ul>
            {favorites?.organizationLocations.map((location, index) => (
              <li key={`fav_organization_location_${index}`}>
                <NextLink href={location?.organization?.uri ?? ''} passHref>
                  <Link color="powerblue" customUnderline>
                    {location.organization?.title}{' '}
                    {location.visitAddressHide === false && (
                      <span>, locatie {location.visitAddress.address}</span>
                    )}
                  </Link>
                </NextLink>
              </li>
            ))}
          </ul>
        </div>
      )}

      {favorites?.facilities?.length > 0 && (
        <div className="mb-8">
          <Heading level={3} className="mb-4">
            Hulpaanbod
          </Heading>
          <ul>
            {favorites?.facilities.map((facility, index) => (
              <li key={`fav_facility_${index}`}>
                <NextLink href={facility?.uri || '/'} passHref>
                  <Link color="powerblue" customUnderline>
                    {facility?.title}
                  </Link>
                </NextLink>
              </li>
            ))}
          </ul>
        </div>
      )}

      {favorites?.facilityLocations?.length > 0 && (
        <div className="mb-8">
          <Heading level={3} className="mb-4">
            Hulpaanbod (locaties)
          </Heading>
          <ul>
            {favorites?.facilityLocations.map((location, index) => (
              <li key={`fav_facility_location_${index}`}>
                <NextLink href={location?.facility?.uri ?? '/'} passHref>
                  <Link color="powerblue" customUnderline>
                    {location.facility?.title}, locatie {location.title}
                  </Link>
                </NextLink>
              </li>
            ))}
          </ul>
        </div>
      )}

      {favorites?.themes?.length > 0 && (
        <div className="mb-8">
          <Heading level={3} className="mb-4">
            Onderwerpen
          </Heading>
          <ul>
            {(favorites?.themes as FavoriteThemeEntryFragment[]).map(
              (theme, index) => (
                <li key={`fav_page_${index}`}>
                  <NextLink href={theme.uri} passHref>
                    <Link color="powerblue" customUnderline>
                      {theme.title}
                    </Link>
                  </NextLink>
                </li>
              )
            )}
          </ul>
        </div>
      )}
    </>
  );
};
